import { useTranslation } from '@jq4race/i18n';
import { createFileRoute, Link, Outlet, redirect, useLocation } from '@tanstack/react-router';

import { getCurrentSession } from '../app/auth-state';

// ── Settings tabs ────────────────────────────────────────────────────────────
//
// The four settings sections (Branding, Billing, Subscription, SMS Templates)
// live under a single tabbed layout. The pathname — not a search param —
// controls the active tab, which keeps the Stripe OAuth redirect target
// (/dashboard/settings/billing) working without backend changes and avoids
// colliding with Billing's own ?connected / ?error query params.
//
// "Billing" manages the org's OWN Stripe Connect account for accepting
// VISITOR payments. "Subscription" (saas-monetization-plans v2) is the org's
// own SaaS subscription to JQRACE4 — a different thing entirely, kept as its
// own clearly-labeled tab so the two are never confused.

interface SettingsTab {
  /** Key under `settings.tabs` in the `common` namespace. */
  labelKey: 'branding' | 'billing' | 'subscription' | 'smsTemplates';
  to: string;
  /** Exact-match only (the index/Branding tab), so sub-routes don't light it up. */
  exact?: boolean;
}

const SETTINGS_TABS: SettingsTab[] = [
  { labelKey: 'branding', to: '/dashboard/settings', exact: true },
  { labelKey: 'billing', to: '/dashboard/settings/billing' },
  { labelKey: 'subscription', to: '/dashboard/settings/subscription' },
  { labelKey: 'smsTemplates', to: '/dashboard/settings/sms-templates' },
];

export const Route = createFileRoute('/_authenticated/dashboard/settings')({
  beforeLoad: () => {
    const session = getCurrentSession();
    if (!session) {
      throw redirect({ to: '/login' });
    }
    if (session.user.role !== 'org_admin') {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: SettingsLayout,
  staticData: {
    breadcrumb: ['Manage', 'Settings'],
  },
});

function SettingsLayout() {
  const { pathname } = useLocation();
  const { t } = useTranslation('common');

  const isActive = (tab: SettingsTab): boolean =>
    tab.exact ? pathname === tab.to : pathname === tab.to || pathname.startsWith(`${tab.to}/`);

  return (
    <div className="space-y-8">
      <header>
        <p className="jq-eyebrow jq-eyebrow--leader mb-3">{t('settings.eyebrow')}</p>
        <h1 className="font-editorial text-[32px] font-medium leading-[1.1] tracking-tight text-jq-navy sm:text-[40px]">
          {t('settings.title')}
        </h1>
        <p className="mt-3 max-w-xl text-[14px] leading-relaxed text-jq-graphite">
          {t('settings.description')}
        </p>

        <nav
          aria-label={t('settings.tabsLabel')}
          className="mt-6 flex items-center gap-6 border-b border-jq-sand/60"
        >
          {SETTINGS_TABS.map((tab) => {
            const active = isActive(tab);
            return (
              <Link
                key={tab.to}
                to={tab.to}
                aria-current={active ? 'page' : undefined}
                className={`-mb-px border-b-[2.5px] px-1 pb-3 text-[14px] transition-colors duration-200 ease-jq ${
                  active
                    ? 'border-jq-navy font-semibold text-jq-navy'
                    : 'border-transparent font-medium text-jq-graphite hover:border-jq-sand hover:text-jq-charcoal'
                }`}
              >
                {t(`settings.tabs.${tab.labelKey}`)}
              </Link>
            );
          })}
        </nav>
      </header>

      <Outlet />
    </div>
  );
}
